import { useState } from "react";
import Icon from "./Icon.jsx";
import { claudeComplete } from "../services/claude.js";

export default function ExerciseRunner({ exercises, onComplete, onBack, gamIntensity = 1 }) {
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState([]);
  const [hearts, setHearts] = useState(5);

  const current = exercises[idx];
  const progress = (idx / exercises.length) * 100;

  const submit = (result) => {
    const next = [...results, result];
    setResults(next);
    const nextHearts = result.correct ? hearts : Math.max(0, hearts - 1);
    if (!result.correct) setHearts(nextHearts);
    setTimeout(() => {
      if (idx + 1 >= exercises.length) {
        onComplete({ results: next, heartsLeft: nextHearts });
      } else {
        setIdx(idx + 1);
      }
    }, 1400);
  };

  return (
    <div style={{ maxWidth: 820, margin: "0 auto", padding: "32px 24px 120px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 32 }}>
        <button
          onClick={onBack}
          className="btn btn-ghost"
          style={{ padding: 8, borderRadius: 999, border: "none" }}
          title="Quit"
          aria-label="Quit exercises"
        >
          <Icon name="close" size={18} />
        </button>
        <div className="progress" style={{ flex: 1, height: 14 }}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <div style={{ display: "flex", gap: 4 }}>
          {Array.from({ length: 5 }).map((_, i) => (
            <span key={i} style={{ opacity: i < hearts ? 1 : 0.2 }}>
              <Icon name="heart" size={18} />
            </span>
          ))}
        </div>
      </div>

      <div className="eyebrow" style={{ marginBottom: 10 }}>
        Question {idx + 1} of {exercises.length}
      </div>

      {current.type === "mcq" && <MCQ key={current.id} ex={current} onSubmit={submit} intensity={gamIntensity} />}
      {current.type === "fill" && <FillBlank key={current.id} ex={current} onSubmit={submit} intensity={gamIntensity} />}
      {current.type === "code" && <CodeExercise key={current.id} ex={current} onSubmit={submit} intensity={gamIntensity} />}
      {current.type === "short" && <ShortAnswer key={current.id} ex={current} onSubmit={submit} intensity={gamIntensity} />}
    </div>
  );
}

function MCQ({ ex, onSubmit, intensity }) {
  const [picked, setPicked] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  const correct = picked === ex.correct;

  const check = () => {
    if (!picked || submitted) return;
    setSubmitted(true);
    const gems = correct ? 10 : 0;
    setTimeout(() => onSubmit({ id: ex.id, correct, gems, userAnswer: picked }), 900);
  };

  return (
    <div className="fade-up">
      <h2
        className="serif"
        style={{
          fontSize: 34,
          letterSpacing: "-0.01em",
          margin: "0 0 28px",
          lineHeight: 1.15,
        }}
      >
        {ex.prompt}
      </h2>
      <div style={{ display: "grid", gap: 10 }}>
        {ex.options.map((o) => {
          const isPicked = picked === o.id;
          const isCorrect = submitted && o.id === ex.correct;
          const isWrong = submitted && isPicked && !correct;
          return (
            <button
              key={o.id}
              disabled={submitted}
              onClick={() => setPicked(o.id)}
              className="card"
              style={{
                padding: "16px 18px",
                textAlign: "left",
                cursor: submitted ? "default" : "pointer",
                display: "flex",
                gap: 14,
                alignItems: "center",
                background: isCorrect
                  ? "var(--moss-wash)"
                  : isWrong
                  ? "#F4D9D9"
                  : isPicked
                  ? "var(--terracotta-wash)"
                  : "#FBF7F0",
                border: isCorrect
                  ? "2px solid var(--moss)"
                  : isWrong
                  ? "2px solid var(--heart)"
                  : isPicked
                  ? "2px solid var(--terracotta)"
                  : "1px solid var(--line)",
                transition: "all 200ms ease",
              }}
            >
              <div
                style={{
                  width: 30,
                  height: 30,
                  borderRadius: 999,
                  flexShrink: 0,
                  border:
                    "1.5px solid " +
                    (isCorrect
                      ? "var(--moss)"
                      : isWrong
                      ? "var(--heart)"
                      : isPicked
                      ? "var(--terracotta)"
                      : "var(--line-2)"),
                  background:
                    isCorrect || isWrong ? (isCorrect ? "var(--moss)" : "var(--heart)") : "transparent",
                  color: "#fff",
                  display: "grid",
                  placeItems: "center",
                  fontWeight: 600,
                  fontSize: 13,
                  fontFamily: "var(--font-mono)",
                }}
              >
                {isCorrect ? (
                  <Icon name="check" size={16} color="#fff" strokeWidth={2.6} />
                ) : isWrong ? (
                  <Icon name="x" size={16} color="#fff" strokeWidth={2.6} />
                ) : (
                  <span style={{ color: isPicked ? "var(--terracotta)" : "var(--ink-3)" }}>
                    {o.id.toUpperCase()}
                  </span>
                )}
              </div>
              <span style={{ fontSize: 15, lineHeight: 1.5 }}>{o.text}</span>
            </button>
          );
        })}
      </div>

      {submitted && <Feedback correct={correct} explain={ex.explain} intensity={intensity} />}

      {!submitted && (
        <div style={{ marginTop: 28, display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn-terracotta" onClick={check} disabled={!picked}>
            Check answer <Icon name="arrow-right" size={14} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );
}

function FillBlank({ ex, onSubmit, intensity }) {
  const [values, setValues] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [correct, setCorrect] = useState(false);

  const check = () => {
    const isCorrect = ex.template.every((seg) => {
      if (seg.kind !== "b") return true;
      const v = (values[seg.id] || "").trim().toLowerCase();
      return ex.accept[seg.id].some((acc) => v.includes(acc.toLowerCase()));
    });
    setCorrect(isCorrect);
    setSubmitted(true);
    setTimeout(
      () => onSubmit({ id: ex.id, correct: isCorrect, gems: isCorrect ? 15 : 0, userAnswer: values }),
      1000
    );
  };

  return (
    <div className="fade-up">
      <h2
        className="serif"
        style={{ fontSize: 30, letterSpacing: "-0.01em", margin: "0 0 24px", lineHeight: 1.2 }}
      >
        {ex.prompt}
      </h2>
      <div
        className="card"
        style={{
          padding: 22,
          fontFamily: "var(--font-mono)",
          fontSize: 15,
          lineHeight: 1.8,
          background: "#FBF7F0",
          whiteSpace: "pre-wrap",
        }}
      >
        {ex.template.map((seg, i) => {
          if (seg.kind === "t") return <span key={i}>{seg.v}</span>;
          return (
            <input
              key={i}
              value={values[seg.id] || ""}
              onChange={(e) => setValues((v) => ({ ...v, [seg.id]: e.target.value }))}
              placeholder={seg.hint}
              disabled={submitted}
              aria-label={seg.hint}
              style={{
                display: "inline-block",
                minWidth: 160,
                padding: "3px 10px",
                margin: "0 2px",
                border: "none",
                borderBottom:
                  "2px dashed " +
                  (submitted ? (correct ? "var(--moss)" : "var(--heart)") : "var(--terracotta-2)"),
                background: submitted
                  ? correct
                    ? "var(--moss-wash)"
                    : "#F4D9D9"
                  : "var(--terracotta-wash)",
                fontFamily: "inherit",
                fontSize: "inherit",
                color: "var(--ink)",
                outline: "none",
                borderRadius: 4,
              }}
            />
          );
        })}
      </div>

      {submitted && <Feedback correct={correct} explain={ex.explain} intensity={intensity} />}
      {!submitted && (
        <div
          style={{
            marginTop: 28,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <div style={{ color: "var(--ink-3)", fontSize: 13 }}>Type into each blank.</div>
          <button
            className="btn btn-terracotta"
            onClick={check}
            disabled={Object.keys(values).length < 3}
          >
            Check answer <Icon name="arrow-right" size={14} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );
}

function CodeExercise({ ex, onSubmit, intensity }) {
  const [value, setValue] = useState(ex.starter);
  const [submitted, setSubmitted] = useState(false);
  const [correct, setCorrect] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  const check = () => {
    const cleaned = value.toLowerCase();
    const mentionsJson = /json/.test(cleaned);
    const keys = ["urgency", "reason"].filter((k) => cleaned.includes(k)).length;
    const isCorrect = mentionsJson && keys >= 2;
    setCorrect(isCorrect);
    setSubmitted(true);
    setTimeout(
      () => onSubmit({ id: ex.id, correct: isCorrect, gems: isCorrect ? 20 : 0, userAnswer: value }),
      1100
    );
  };

  return (
    <div className="fade-up">
      <h2
        className="serif"
        style={{ fontSize: 30, letterSpacing: "-0.01em", margin: "0 0 12px", lineHeight: 1.2 }}
      >
        {ex.prompt}
      </h2>
      <div style={{ display: "flex", gap: 10, marginBottom: 16 }}>
        <span className="chip">
          <Icon name="sparkle" size={12} /> Code challenge
        </span>
        <button
          className="chip"
          style={{
            cursor: "pointer",
            background: hintOpen ? "var(--gold-wash)" : "var(--cream-2)",
          }}
          onClick={() => setHintOpen((o) => !o)}
        >
          💡 {hintOpen ? "Hide hint" : "Show hint"}
        </button>
      </div>
      {hintOpen && (
        <div
          className="fade-up"
          style={{
            padding: "10px 14px",
            background: "var(--gold-wash)",
            borderRadius: 8,
            fontSize: 13,
            marginBottom: 16,
            color: "var(--ink-2)",
          }}
        >
          {ex.hint}
        </div>
      )}
      <div
        style={{
          position: "relative",
          borderRadius: 12,
          overflow: "hidden",
          boxShadow: "var(--shadow-md)",
        }}
      >
        <div
          style={{
            display: "flex",
            gap: 6,
            padding: "10px 14px",
            background: "#14110D",
            borderBottom: "1px solid #2a241d",
          }}
        >
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#E85D5D" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#E8B04F" }} />
          <span style={{ width: 10, height: 10, borderRadius: 999, background: "#6BBB72" }} />
          <span className="mono" style={{ marginLeft: 10, fontSize: 11, color: "#8B8379" }}>
            prompt.md
          </span>
        </div>
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          disabled={submitted}
          spellCheck={false}
          aria-label="Prompt editor"
          style={{
            width: "100%",
            minHeight: 220,
            padding: 18,
            border: "none",
            outline: "none",
            background: "#1F1B16",
            color: "#E8DFCD",
            fontFamily: "var(--font-mono)",
            fontSize: 13,
            lineHeight: 1.7,
            resize: "vertical",
          }}
        />
      </div>

      {submitted && <Feedback correct={correct} explain={ex.explain} intensity={intensity} />}
      {!submitted && (
        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn-terracotta" onClick={check}>
            Run check <Icon name="arrow-right" size={14} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );
}

function ShortAnswer({ ex, onSubmit, intensity }) {
  const [value, setValue] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [grading, setGrading] = useState(false);
  const [grade, setGrade] = useState(null);

  const check = async () => {
    if (!value.trim() || submitted) return;
    setGrading(true);
    setSubmitted(true);
    try {
      const res = await claudeComplete({
        messages: [
          {
            role: "user",
            content: `You are a warm, encouraging tutor grading a short written answer in a prompt engineering course.

QUESTION: ${ex.prompt}

RUBRIC (what a correct answer must demonstrate): ${ex.rubric}

LEARNER'S ANSWER:
"""${value}"""

Grade it. Reply with STRICT JSON only, matching this shape:
{
  "correct": boolean,
  "score": number,
  "feedback": "1-2 sentences, warm and specific"
}
Return nothing else.`,
          },
        ],
      });
      const m = res.match(/\{[\s\S]*\}/);
      if (m) {
        const parsed = JSON.parse(m[0]);
        setGrade(parsed);
        setTimeout(
          () =>
            onSubmit({
              id: ex.id,
              correct: parsed.correct,
              gems: parsed.correct ? 25 : 5,
              userAnswer: value,
              score: parsed.score,
            }),
          1800
        );
      } else throw new Error("no json");
    } catch {
      const words = value.trim().split(/\s+/).length;
      const hasGuess = /guess|halluc|fill|make\s*up|invent/i.test(value);
      const hasFix = /skip|mark|flag|ask|tbd|unsupported|don'?t|explicit/i.test(value);
      const ok = words >= 15 && hasGuess && hasFix;
      const fb = {
        correct: ok,
        score: ok ? 85 : 55,
        feedback: ok
          ? "Nice — you named both the failure mode and the fix."
          : "Good start. Try to mention what the model does without guidance (guessing) AND the specific fallback you'd write.",
      };
      setGrade(fb);
      setTimeout(
        () =>
          onSubmit({
            id: ex.id,
            correct: ok,
            gems: ok ? 25 : 5,
            userAnswer: value,
            score: fb.score,
          }),
        1400
      );
    }
    setGrading(false);
  };

  return (
    <div className="fade-up">
      <h2
        className="serif"
        style={{ fontSize: 30, letterSpacing: "-0.01em", margin: "0 0 14px", lineHeight: 1.2 }}
      >
        {ex.prompt}
      </h2>
      <div
        className="chip"
        style={{
          marginBottom: 18,
          background: "var(--gem-wash)",
          borderColor: "var(--gem)",
        }}
      >
        <Icon name="sparkle" size={12} color="#2F5562" /> Graded by Claude
      </div>
      <textarea
        value={value}
        onChange={(e) => setValue(e.target.value)}
        disabled={submitted}
        placeholder="Type your answer here…"
        aria-label="Your answer"
        style={{
          width: "100%",
          minHeight: 140,
          padding: 16,
          borderRadius: 12,
          border: "1px solid var(--line)",
          background: "#FBF7F0",
          fontFamily: "var(--font-sans)",
          fontSize: 15,
          lineHeight: 1.6,
          outline: "none",
          resize: "vertical",
          color: "var(--ink)",
        }}
      />
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: 12,
          color: "var(--ink-3)",
          marginTop: 8,
        }}
      >
        <span>
          {value.trim() ? value.trim().split(/\s+/).length : 0} words · aim for 2–3 sentences
        </span>
        <span>Earn up to 25 💎</span>
      </div>

      {grading && (
        <div
          style={{
            marginTop: 22,
            padding: 18,
            borderRadius: 12,
            background: "var(--gem-wash)",
            display: "flex",
            alignItems: "center",
            gap: 12,
          }}
        >
          <div className="pulse">
            <Icon name="sparkle" size={20} color="#2F5562" />
          </div>
          <span style={{ fontWeight: 500 }}>Claude is reading your answer…</span>
        </div>
      )}

      {grade && !grading && (
        <div
          className="fade-up"
          style={{
            marginTop: 22,
            padding: 22,
            borderRadius: 14,
            background: grade.correct ? "var(--moss-wash)" : "var(--terracotta-wash)",
            border: "1px solid " + (grade.correct ? "var(--moss)" : "var(--terracotta-2)"),
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
            <span style={{ fontSize: 14, fontWeight: 600 }}>
              {grade.correct ? "✨ Great answer" : "Almost there"}
            </span>
            <span
              className="mono"
              style={{
                fontSize: 12,
                background: "#FBF7F0",
                padding: "2px 8px",
                borderRadius: 999,
              }}
            >
              Score: {grade.score}/100
            </span>
          </div>
          <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)" }}>{grade.feedback}</div>
          <div
            style={{
              marginTop: 12,
              fontSize: 13,
              color: "var(--ink-3)",
              fontStyle: "italic",
            }}
          >
            Reference: {ex.explain}
          </div>
        </div>
      )}

      {!submitted && (
        <div style={{ marginTop: 20, display: "flex", justifyContent: "flex-end" }}>
          <button className="btn btn-terracotta" onClick={check} disabled={!value.trim()}>
            Submit to Claude <Icon name="sparkle" size={14} color="#fff" />
          </button>
        </div>
      )}
    </div>
  );
}

function Feedback({ correct, explain, intensity = 1 }) {
  return (
    <div
      className="fade-up"
      style={{
        marginTop: 22,
        padding: 20,
        borderRadius: 14,
        background: correct ? "var(--moss-wash)" : "#F4D9D9",
        border: "1px solid " + (correct ? "var(--moss)" : "var(--heart)"),
        position: "relative",
        overflow: "hidden",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
        {correct ? (
          <>
            <span style={{ fontSize: 15, fontWeight: 600, color: "#3E5940" }}>✨ Nice work!</span>
            {intensity >= 1 && (
              <span className="chip" style={{ background: "#FBF7F0", borderColor: "var(--gem)" }}>
                <Icon name="gem" size={12} /> +10
              </span>
            )}
            {intensity >= 2 && (
              <span className="chip" style={{ background: "#FBF7F0", borderColor: "var(--gold)" }}>
                <Icon name="bolt" size={12} /> +25 XP
              </span>
            )}
          </>
        ) : (
          <span style={{ fontSize: 15, fontWeight: 600, color: "#8A2A2A" }}>
            Not quite — don't worry.
          </span>
        )}
      </div>
      <div style={{ fontSize: 14, lineHeight: 1.55, color: "var(--ink-2)" }}>{explain}</div>
      {correct && intensity >= 2 && <ConfettiBurst />}
    </div>
  );
}

function ConfettiBurst() {
  const bits = Array.from({ length: 14 }, (_, i) => ({
    c: ["#C96442", "#C8953B", "#4B7B8A", "#6B8E6E", "#1F1B16"][i % 5],
    x: (Math.random() - 0.5) * 400,
    d: Math.random() * 300,
    r: Math.random() * 8 + 4,
  }));
  return (
    <div style={{ position: "absolute", inset: 0, pointerEvents: "none", overflow: "hidden" }}>
      {bits.map((b, i) => (
        <span
          key={i}
          style={{
            position: "absolute",
            top: -10,
            left: `calc(50% + ${b.x}px)`,
            width: b.r,
            height: b.r,
            background: b.c,
            borderRadius: 2,
            animation: `confetti 1.2s ease-in ${b.d}ms both`,
          }}
        />
      ))}
    </div>
  );
}
